/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { useDebouncedCallback } from 'utils/hooks/useDebounce'

import UiFormLabel from './UiFormLabel'
import UiSelectOptionsList from './UiSelectComponents/UiSelectOptionsList'

export interface UiSelectOption {
    value: string | number
    label: string
}

interface UiSelectProps {
    options: UiSelectOption[]
    value: string | number | null
    onChange: (value: string | number) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    error?: string
    required?: boolean
    ariaLabel?: string
    debounceTime?: number
}

const UiSelect: React.FC<UiSelectProps> = ({
    options,
    value,
    onChange,
    label,
    placeholder = 'Select an option',
    disabled,
    className,
    error,
    required,
    ariaLabel,
    debounceTime = 300,
}) => {
    const selectedOption = options.find((opt) => opt.value === value)
    const hasError = !!error
    const displayText = selectedOption ? selectedOption.label : placeholder

    // Debounce the onChange callback to improve performance
    const debouncedOnChange = useDebouncedCallback((newValue: string | number) => onChange(newValue), debounceTime)

    return (
        <div className={clsx('UiSelect flex flex-col gap-1.5', className)}>
            {label && <UiFormLabel isRequired={required}>{label}</UiFormLabel>}
            <Listbox value={value} onChange={debouncedOnChange} disabled={disabled}>
                <div className="relative">
                    <Listbox.Button
                        aria-label={ariaLabel ?? label ?? placeholder}
                        className={clsx(
                            'relative w-full cursor-default rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm shadow-sm transition-all focus:outline-none focus-visible:border-blue-500 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-950',
                            hasError
                                ? 'border-red-500 focus-visible:border-blue-500'
                                : 'border-gray-300 dark:border-gray-800',
                            !selectedOption && 'text-gray-700'
                        )}
                    >
                        <span className="block truncate">{displayText}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                        </span>
                    </Listbox.Button>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options
                            anchor="bottom start"
                            className="z-9999 mt-1 max-h-60 w-(--button-width) overflow-auto overscroll-contain rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900 dark:ring-white/10 transition duration-100 ease-in data-closed:opacity-0"
                        >
                            <UiSelectOptionsList options={options} />
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
            {hasError && <span className="text-xs text-red-500 mt-1">{error}</span>}
        </div>
    )
}

export default UiSelect
